import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import AlertDialogSlide from 'common/components/dialog/dialog';
import {
  Checkbox,
  Typography,
  Box,
  TextField,
  Grid,
  DialogActions,
  FormControlLabel,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from '@material-ui/core';
import PrimaryButton from 'common/components/buttons/primaryButton';
import SecondaryButton from 'common/components/buttons/secondaryButton';

function ManageInheritedActivitiesModal({
  open,
  onClose,
  stationName,
  activities,
  selectedActionIds,
  loading,
  saving,
  onSave,
}) {
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [filterText, setFilterText] = useState('');

  // paginação
  const [pageAvailable, setPageAvailable] = useState(0);
  const [rowsPerPageAvailable, setRowsPerPageAvailable] = useState(5);

  const [pageSelected, setPageSelected] = useState(0);
  const [rowsPerPageSelected, setRowsPerPageSelected] = useState(5);

  useEffect(() => {
    if (open) {
      setSelectedActivities(selectedActionIds || []);
      setFilterText('');
      setPageAvailable(0);
      setPageSelected(0);
    }
  }, [open, selectedActionIds]);

  const handleToggle = (activityId) => {
    setSelectedActivities((prev) =>
      prev.includes(activityId) ? prev.filter((id) => id !== activityId) : [...prev, activityId]
    );
  };

  const availableList = useMemo(() => {
    return activities
      .filter((a) => !selectedActivities.includes(a.id))
      .filter((a) => a.name.toLowerCase().includes(filterText.toLowerCase()));
  }, [activities, selectedActivities, filterText]);

  const selectedList = useMemo(
    () => activities.filter((a) => selectedActivities.includes(a.id)),
    [activities, selectedActivities]
  );

  // paginação slices
  const paginatedAvailable = useMemo(
    () =>
      availableList.slice(
        pageAvailable * rowsPerPageAvailable,
        pageAvailable * rowsPerPageAvailable + rowsPerPageAvailable
      ),
    [availableList, pageAvailable, rowsPerPageAvailable]
  );

  const paginatedSelected = useMemo(
    () =>
      selectedList.slice(
        pageSelected * rowsPerPageSelected,
        pageSelected * rowsPerPageSelected + rowsPerPageSelected
      ),
    [selectedList, pageSelected, rowsPerPageSelected]
  );

  const handleSelectAll = () => {
    const filteredIds = availableList.map((a) => a.id);
    const allSelected = filteredIds.every((id) => selectedActivities.includes(id));

    if (allSelected) {
      setSelectedActivities((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedActivities((prev) => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const allAvailableSelectedState = useMemo(() => {
    const filteredIds = availableList.map((a) => a.id);
    if (filteredIds.length === 0) return { checked: false, indeterminate: false };

    const selectedCount = filteredIds.filter((id) => selectedActivities.includes(id)).length;

    return {
      checked: selectedCount === filteredIds.length,
      indeterminate: selectedCount > 0 && selectedCount < filteredIds.length,
    };
  }, [availableList, selectedActivities]);

  const handleDeselectAll = () => {
    setSelectedActivities([]);
  };

  const handleSave = () => {
    onSave(selectedActivities);
    onClose();
  };

  // reset página ao filtrar
  useEffect(() => {
    setPageAvailable(0);
  }, [filterText]);

  const renderRows = (list) => {
    if (loading) {
      return (
        <TableRow>
          <TableCell>
            <Typography variant="body2">Carregando...</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return list.map((row) => (
      <TableRow
        key={row.id}
        hover
        onClick={() => handleToggle(row.id)}
        style={{ cursor: 'pointer', height: 36 }}>
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={selectedActivities.includes(row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleToggle(row.id)}
            color="primary"
          />
        </TableCell>

        <TableCell>
          <Typography variant="body2">{row.name}</Typography>

          <Typography variant="caption" style={{ color: '#666' }}>
            {row.createdAt ? new Date(row.createdAt).toLocaleString('pt-BR') : ''}
          </Typography>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <AlertDialogSlide
      visible={open}
      onClose={onClose}
      title={<Typography variant="h6">Gerenciar Atividades Herdadas</Typography>}
      maxWidth="lg">
      <Box display="flex" flexDirection="column" height="70vh">
        <Box flex={1} overflow="auto" pr={1}>
          <Typography variant="body2" gutterBottom>
            Selecione quais atividades <strong>{stationName}</strong> deve herdar.
          </Typography>

          <Grid container spacing={2}>
            {/* Disponíveis */}
            <Grid item xs={12} md={6}>
              <Box mb={1}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Filtrar atividades disponíveis..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </Box>

              {availableList.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      color="primary"
                      checked={allAvailableSelectedState.checked}
                      indeterminate={allAvailableSelectedState.indeterminate}
                      onChange={handleSelectAll}
                    />
                  }
                  label="Selecionar todos"
                />
              )}

              <Typography variant="subtitle2">Disponíveis ({availableList.length})</Typography>

              <Table size="small">
                <TableBody>{renderRows(paginatedAvailable)}</TableBody>
              </Table>

              <TablePagination
                component="div"
                count={availableList.length}
                page={pageAvailable}
                onPageChange={(e, newPage) => setPageAvailable(newPage)}
                rowsPerPage={rowsPerPageAvailable}
                onRowsPerPageChange={(e) => {
                  setRowsPerPageAvailable(parseInt(e.target.value, 10));
                  setPageAvailable(0);
                }}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                labelRowsPerPage="Linhas por página"
              />
            </Grid>

            {/* Selecionadas */}
            <Grid item xs={12} md={6}>
              {selectedList.length > 0 && (
                <Box display="flex" justifyContent="flex-end">
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={selectedList.length > 0}
                        onChange={handleDeselectAll}
                      />
                    }
                    label="Desmarcar todos"
                  />
                </Box>
              )}

              <Typography variant="subtitle2">Selecionadas ({selectedList.length})</Typography>

              <Table size="small">
                <TableBody>{renderRows(paginatedSelected)}</TableBody>
              </Table>

              <TablePagination
                component="div"
                count={selectedList.length}
                page={pageSelected}
                onPageChange={(e, newPage) => setPageSelected(newPage)}
                rowsPerPage={rowsPerPageSelected}
                onRowsPerPageChange={(e) => {
                  setRowsPerPageSelected(parseInt(e.target.value, 10));
                  setPageSelected(0);
                }}
                rowsPerPageOptions={[5, 10, 20, 50, 100]}
                labelRowsPerPage="Linhas por página"
              />
            </Grid>
          </Grid>
        </Box>

        <DialogActions
          style={{
            borderTop: '1px solid #e0e0e0',
            padding: '12px 24px',
            background: '#fff',
          }}>
          <SecondaryButton onClick={onClose} disabled={saving}>
            Cancelar
          </SecondaryButton>

          <PrimaryButton onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Salvando...' : 'Salvar seleção'}
          </PrimaryButton>
        </DialogActions>
      </Box>
    </AlertDialogSlide>
  );
}

ManageInheritedActivitiesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  stationName: PropTypes.string.isRequired,
  activities: PropTypes.array.isRequired,
  selectedActionIds: PropTypes.array,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

export default ManageInheritedActivitiesModal;
